import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PackagePlus, PackageMinus, SlidersHorizontal, Search } from "lucide-react";
import { toast } from "sonner";
import { FoodSelect, PageHead, apiErr, fmtDate, fmtQ, newReqId, toNum, todayISO, useMerendaMeta } from "@/merenda/common";
import { MovementsDialog } from "@/merenda/MovementsDialog";
import { StockAlerts } from "@/merenda/StockAlerts";

const InitialStock = ({ foods, scopeParams, onDone }) => {
  const [date, setDate] = useState(todayISO());
  const [resp, setResp] = useState("");
  const [qty, setQty] = useState({});
  const [q, setQ] = useState("");
  const save = async () => {
    const items = Object.entries(qty).map(([food_id, v]) => ({ food_id, qty: toNum(v) })).filter((i) => i.qty > 0);
    if (!resp.trim()) return toast.error("Informe o responsável");
    try { await api.post("/merenda/initial-stock", { date, responsible: resp, items }, { params: scopeParams }); toast.success("Estoque inicial registrado"); onDone(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Card className="mb-6 border-orange-200 bg-orange-50/40 p-4" data-testid="initial-stock-card">
      <h3 className="font-display font-semibold text-slate-800">Estoque inicial (primeira utilização)</h3>
      <p className="mb-3 text-xs text-slate-500">Cadastrado uma única vez. Depois, o saldo inicial de cada mês vem do saldo final do mês anterior. Mapas históricos importados não geram movimentação.</p>
      <div className="mb-3 grid gap-2 sm:grid-cols-3">
        <div><Label>Data</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="initial-date" /></div>
        <div><Label>Responsável</Label><Input value={resp} onChange={(e) => setResp(e.target.value)} data-testid="initial-responsible" /></div>
        <div><Label>Filtrar</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alimento" /></div>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-lg border bg-white">
        {foods.filter((f) => f.active !== false && f.name.toLowerCase().includes(q.toLowerCase())).map((f) => (
          <div key={f.id} className="flex items-center justify-between border-b px-3 py-1.5 text-sm">
            <span>{f.name}</span>
            <span className="flex items-center gap-1"><Input className="h-8 w-24" inputMode="decimal" value={qty[f.id] || ""} onChange={(e) => setQty({ ...qty, [f.id]: e.target.value })} data-testid={`initial-qty-${f.id}`} /><span className="w-8 text-xs text-slate-500">{f.unit}</span></span>
          </div>
        ))}
      </div>
      <Button className="mt-3" onClick={save} data-testid="initial-save">Registrar estoque inicial</Button>
    </Card>
  );
};

const MoveDialog = ({ kind, foods, scopeParams, onClose, onDone }) => {
  const [f, setF] = useState({ date: todayISO(), food_id: "", qty: "", reason: "", sign: "-" });
  const [rid] = useState(newReqId());
  const unit = foods.find((x) => x.id === f.food_id)?.unit;
  const save = async () => {
    try {
      if (kind === "perda") await api.post("/merenda/losses", { date: f.date, food_id: f.food_id, qty: toNum(f.qty), reason: f.reason, request_id: rid }, { params: scopeParams });
      else await api.post("/merenda/adjustments", { date: f.date, food_id: f.food_id, delta: (f.sign === "-" ? -1 : 1) * toNum(f.qty), reason: f.reason, request_id: rid }, { params: scopeParams });
      toast.success(kind === "perda" ? "Perda registrada" : "Ajuste registrado"); onDone();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{kind === "perda" ? "Registrar perda" : "Ajuste de inventário"}</DialogTitle></DialogHeader>
        <div><Label>Data</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid={`${kind}-date`} /></div>
        <div><Label>Alimento</Label><FoodSelect foods={foods} value={f.food_id} onChange={(v) => setF({ ...f, food_id: v })} testid={`${kind}-food`} /></div>
        <div className="flex items-end gap-2">
          {kind === "ajuste" && <select className="h-10 rounded-md border px-2" value={f.sign} onChange={(e) => setF({ ...f, sign: e.target.value })} data-testid="ajuste-sign"><option value="-">Reduzir</option><option value="+">Aumentar</option></select>}
          <div className="flex-1"><Label>Quantidade {unit && `(${unit})`}</Label><Input inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid={`${kind}-qty`} /></div>
        </div>
        <div><Label>Motivo / justificativa</Label><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid={`${kind}-reason`} /></div>
        <DialogFooter><Button onClick={save} data-testid={`${kind}-save`}>Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Estoque() {
  const { meta, foods, scopeParams, canManage } = useMerendaMeta();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [mov, setMov] = useState(null);
  const load = () => api.get("/merenda/stock", { params: { date, ...scopeParams } }).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { if (meta) load(); }, [meta, date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!meta || !data) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  const rows = data.items.filter((i) => i.name.toLowerCase().includes(q.toLowerCase()) && (i.active || i.balance));
  return (
    <div data-testid="estoque-page">
      <PageHead title="Estoque" subtitle={data.has_initial ? `Estoque inicial cadastrado em ${fmtDate(data.initial_date)}` : "Estoque inicial ainda não cadastrado"}>
        {canManage && <><Button variant="outline" onClick={() => setDlg("perda")} data-testid="estoque-loss-btn"><PackageMinus className="mr-1 h-4 w-4" /> Perda</Button>
          <Button variant="outline" onClick={() => setDlg("ajuste")} data-testid="estoque-adjust-btn"><SlidersHorizontal className="mr-1 h-4 w-4" /> Ajuste</Button></>}
      </PageHead>
      <StockAlerts scopeParams={scopeParams} reloadKey={data.items.length} />
      {canManage && !data.has_initial && <InitialStock foods={foods} scopeParams={scopeParams} onDone={load} />}
      <div className="mb-3 flex flex-wrap gap-2">
        <div><Label className="text-xs">Saldo em</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="estoque-date" /></div>
        <div className="relative flex-1"><Label className="text-xs">Buscar</Label><Search className="absolute bottom-3 left-2 h-4 w-4 text-slate-400" /><Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)} data-testid="estoque-search" /></div>
      </div>
      <Card className="overflow-x-auto border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Alimento</TableHead><TableHead>Unid.</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={r.food_id} className="cursor-pointer hover:bg-slate-50" onClick={() => setMov(r)} data-testid={`estoque-row-${r.food_id}`}>
              <TableCell className="font-medium">{r.name}</TableCell><TableCell>{r.unit}</TableCell>
              <TableCell className={`text-right font-semibold ${r.balance < 0 ? "text-red-600" : r.balance > 0 ? "text-slate-900" : "text-slate-400"}`}>{fmtQ(r.balance)}</TableCell>
            </TableRow>))}</TableBody>
        </Table>
      </Card>
      {dlg && <MoveDialog kind={dlg} foods={foods} scopeParams={scopeParams} onClose={() => setDlg(null)} onDone={() => { setDlg(null); load(); }} />}
      {mov && <MovementsDialog food={mov} scopeParams={scopeParams} onClose={() => setMov(null)} />}
    </div>
  );
}
